import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {requestStores, useApplicationStore} from '../../ListOfPREditors.brick.jsx';
import {CurrencyController} from '../../../controllers/CurrencyController';
import produce from 'immer';
import {formatCurrencySeparator} from '../../../utils/number.js';
import {IconButton, Typography, Checkbox, Link} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {ezprOrange} from '../../_helpers.js';
import {TRANSPORT_OPTIONS_ENUM} from '../../../utils/constants.js';
import CircularProgress from '@mui/material/CircularProgress';
import {BrickContext} from 'olympe';
import {OneTimeVendor} from '../../../models/OneTimeVendor';
import {useFieldEditability} from '../utils/useFieldEditability.js';

/**
 * @param {TotalBudget} props
 * @return {JSX.Element}
 * @constructor
 */
export function TotalBudget(props) {
    const useRequestStore = requestStores.get(props.prTag);
    const getTotalPrice = useRequestStore((state) => state.getTotalPrice);
    const companyCode = useRequestStore((state) => state.companyCode);
    const getTotalPriceWithoutCurrencyExchange = useRequestStore((state) => state.getTotalPriceWithoutCurrencyExchange);
    const materials = useRequestStore((state) => state.materials);
    const companyCodeCurrency = useRequestStore((state) => state.companyCodeCurrency);
    const supplierStatus = useRequestStore((state) => state.supplierStatus);
    const compCodeCurrToThresholdCurrExchangeRate = useRequestStore((state) => state.compCodeCurrToThresholdCurrExchangeRate);
    const updateAllMaterials = useRequestStore((state) => state.updateAllMaterials);
    const [conversionFailed, setConversionFailed] = useRequestStore((state) => [state.conversionFailed, state.setConversionFailed]);
    const separator = useApplicationStore((state) => state.userCurrencySeparator);
    const userIdToCallSAPAPIs = useRequestStore((state) => state.userIdToCallSAPAPIs);
    const getTotalConditionsPrice = useRequestStore((state)=>state.getTotalConditionsPrice);
    const getTotalConditionPriceForMaterial = useRequestStore((state)=>state.getTotalConditionPriceForMaterial);
    const materialsReady = useRequestStore((state)=>state.materialsReady);
    const cbThresholdCurrency = useRequestStore((state) => state.cbThresholdCurrency);
    const supplierType = useRequestStore((state) => state.supplierType);
    const [mtvPowerBIValidated, setMtvPowerBIValidated] = useRequestStore((state) => [
        state.mtvPowerBIValidated,
        state.setMtvPowerBIValidated,
    ]);
    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});
    const mtvValidatedDisabled = isDisabled('actions.mtvPowerBIValidated');


    // Show 5 decimals if a material currency is USDN.
    const materialsIncludeUsdn = materials.some((material) => material?.currency?.value === 'USDN');
    const totalPriceDecimals = materialsIncludeUsdn ? 5 : 2;
    const totalWithoutTaxes = getTotalPrice().toFixed(totalPriceDecimals);
    const totalWithoutTaxesAndExchange = getTotalPriceWithoutCurrencyExchange().toFixed(totalPriceDecimals);

    const [showMaterials, setShowMaterials] = useState(false);
    const [isFetchingRate, setIsFetchingRate] = useState(false);

    const showTransportCostConditions = materials[materials.length -1].transportCostType === TRANSPORT_OPTIONS_ENUM['Via Condition type'] || false;

    const showTaxAmount =
        companyCode.value.substring(0, 2) === 'US' &&
        materials.some((m) => m.taxCode?.value === 'I1');

    const totalTaxAmount = materials.reduce((total, material) => {
        if (material.taxAmount !== undefined && material.taxAmount !== null) {
            return total + Number(material.taxAmount);
        }
        return total;
    }, 0);

    const POWER_BI_REPORT_URL = 'https://nestle.sharepoint.com/teams/ProcOpsITPPACollab/Lists/Procurement%20Operations%20New%20Requirements%20Tracker%20v2/AllItems.aspx?FilterField1=Application&FilterValue1=EasyRequest&FilterType1=MultiChoice&FilterFields2=Status&FilterValues2=0%2E%20Draft%3B%231%2E%20Submitted%20by%20PPA%3B%233%2E%20Needs%20further%20clarification%3B%237%2E%20Testing&FilterTypes2=Choice&viewid=dc993f26%2D50f6%2D460a%2D9e1c%2D41f9984afba8&ovuser=12a3af23%2Da769%2D4654%2D847f%2D958f3d479f4a%2CVelrajan%2ES%40aoa%2Enestle%2Ecom&OR=Teams%2DHL&CT=1756131998535&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiI0OS8yNTA3MzExNzQxMiIsIkhhc0ZlZGVyYXRlZFVzZXIiOmZhbHNlfQ%3D%3D';

    useEffect(() => {
        if (!materialsReady) return;

        if (companyCodeCurrency) {
            const abortController = new AbortController();
            let cancelled = false;

            const fetchExchangeRates = async () => {
                if (cancelled) return;
                setIsFetchingRate(true);

                let exchangeValues = [];
                let isAborted = false;

                const newMaterials = await produce(materials, async (draft) => {
                    // we check if any other material has a different currency and not empty string and filter out company code currency.
                    const currencies = new Set(draft.map((material) => material.currency.value).filter((val) => val && val !== companyCodeCurrency));
                    if (currencies.length > 1) {
                        if (!cancelled) setConversionFailed('');
                    } else {
                        const fetchRates = async () => {
                            for (let it = currencies.values(), val = null; val = it.next().value;) {
                                if (abortController.signal.aborted) {
                                    isAborted = true;
                                    throw new Error('Fetch aborted');
                                }
                                try {
                                    const result = await CurrencyController.getCurrencyExchangeRate(val, companyCodeCurrency, userIdToCallSAPAPIs, props.context);
                                    exchangeValues.push(result);
                                } catch (error) {
                                    if (abortController.signal.aborted) {
                                        isAborted = true;
                                        console.error('Fetch aborted');
                                        return;
                                    }
                                    console.error(error.message);
                                }
                            }
                            if (!cancelled) setConversionFailed('');
                        };

                        await fetchRates().catch((error) => {
                            console.log(error.message);
                            exchangeValues = [];
                            if (!cancelled) setConversionFailed(error.currency);
                        });
                    }
                });

                const materialsWithExchangeRates = newMaterials.map((mat) => {
                    const newRateValue = exchangeValues.find((data) => data.currency === mat.currency.value);
                    return {
                        ...mat,
                        exchangeCurrencyRate: newRateValue ? newRateValue.rate : 1.0,
                    };
                });

                if (!isAborted && !cancelled) {
                    updateAllMaterials(materialsWithExchangeRates);
                }
                if (!cancelled) setIsFetchingRate(false);
            };

            fetchExchangeRates();

            return () => {
                cancelled = true;
                abortController.abort();
            };
        }
    }, [companyCodeCurrency, JSON.stringify(materials.map((m) => m.currency.value))]);


    return (
        <div className={'pr-editor-centered-submodule'}>
            <p className={'pr-editor-section-title'} >Total Request Amount</p>
            <div className={'pr-editor-total-value'}>
                <div className='pr-editor-total-value-row' style={{display: 'flex', alignItems: 'center'}}>
                    <span style={{minWidth: '40px'}} />
                    <span style={{minWidth: '90px', maxWidth: '90px'}} />
                    <Typography className='pr-editor-total-value-row-item' variant="body1">Request currency</Typography>
                    <Typography className='pr-editor-total-value-row-item' variant="body1">Converted currency</Typography>
                    {showTransportCostConditions && <Typography className='pr-editor-total-value-row-item' variant="body1">Total with conditions</Typography>}
                    {showTaxAmount && (<Typography className='pr-editor-total-value-row-item' variant="body1">Total tax</Typography>)}
                    {supplierType === OneTimeVendor.supplierTypes.mtv && (
                        <div className="pr-editor-total-actions">
                            <Link
                                component="button"
                                type="button"
                                onClick={() => window.open(POWER_BI_REPORT_URL, '_blank')}
                                sx={{
                                    'color': ezprOrange,
                                    'textDecoration': 'none',
                                    '&:hover': {textDecoration: 'underline'},
                                }}
                            >
                                MTV Annual Spend Report
                            </Link>
                        </div>
                    )}
                    {isFetchingRate && <CircularProgress size={18}/>}
                </div>
                <div className='pr-editor-total-value-row'>
                    <span style={{minWidth: '40px'}} >
                        <IconButton
                            onClick={()=>{
                                setShowMaterials((prevState)=> !prevState);
                            }}
                            sx={{
                                color: ezprOrange,
                            }}
                        >
                            {!showMaterials && <AddIcon />}
                            {showMaterials && <RemoveIcon />}
                        </IconButton>
                    </span>
                    <Typography style={{minWidth: '90px', maxWidth: '90px'}} className='pr-editor-total-value-row-item' variant="body1">
                            Total
                    </Typography>
                    <Typography className='pr-editor-total-value-row-item' variant="body1">
                        {`${formatCurrencySeparator(totalWithoutTaxesAndExchange, separator, totalPriceDecimals)} ${materials[0].currency.value}`}
                    </Typography>
                    <Typography className='pr-editor-total-value-row-item' variant="body1" sx={{fontWeight: 'bold'}}>
                        {((conversionFailed && (supplierStatus === 'Blank' || supplierStatus === 'Validated' || !supplierStatus)) ||
                            (!conversionFailed && cbThresholdCurrency === materials[0].currency.value)) && (
                            <span>
                                {`${formatCurrencySeparator(totalWithoutTaxesAndExchange, separator, totalPriceDecimals)} ${materials[0].currency.value}`}
                            </span>
                        )}
                        {(!conversionFailed && cbThresholdCurrency !== materials[0].currency.value) && (
                            <span>
                                {formatCurrencySeparator((parseFloat(totalWithoutTaxes) * compCodeCurrToThresholdCurrExchangeRate).toFixed(3), separator, totalPriceDecimals)} {cbThresholdCurrency}
                            </span>
                        )}
                    </Typography>
                    {showTransportCostConditions && <Typography className='pr-editor-total-value-row-item' variant="body1">
                        {`${formatCurrencySeparator(Number(totalWithoutTaxesAndExchange) + getTotalConditionsPrice(), separator, totalPriceDecimals)} ${materials[0].currency.value}`}
                    </Typography>}
                    {showTaxAmount && (
                        <Typography className='pr-editor-total-value-row-item' variant="body1">
                            {`${formatCurrencySeparator(totalTaxAmount.toFixed(totalPriceDecimals), separator, totalPriceDecimals)} ${materials[0].currency.value}`}
                        </Typography>
                    )}
                    {supplierType === OneTimeVendor.supplierTypes.mtv && (
                        <div className="pr-editor-total-actions">
                            <div className="pr-editor-total-action-checkbox">
                                <Checkbox
                                    checked={mtvPowerBIValidated}
                                    onChange={(e) => setMtvPowerBIValidated(e.target.checked)}
                                    disabled={mtvValidatedDisabled}
                                    sx={{
                                        'padding': 0,
                                        'color': ezprOrange,
                                        '&.Mui-checked': {color: ezprOrange},
                                    }}
                                />
                                <Typography
                                    variant="body1"
                                    onClick={
                                        mtvValidatedDisabled ?
                                            undefined :
                                            () => setMtvPowerBIValidated(!mtvPowerBIValidated)
                                    }
                                    sx={{
                                        cursor: mtvValidatedDisabled ? 'default' : 'pointer',
                                    }}
                                >
                                    I validate
                                </Typography>
                            </div>
                        </div>
                    )}
                </div>
                {showMaterials &&
                        materials.map((material, index) => {
                            const subtotalDecimals = material.currency?.value === 'USDN' ? 5 : 2;
                            const taxValue = material.taxAmount !== undefined && material.taxAmount !== null ? Number(material.taxAmount) : 0;
                            return (
                                <div className='pr-editor-total-value-row' key={'material-price-' + material.id}>
                                    <span style={{minWidth: '40px'}} />
                                    <Typography style={{minWidth: '90px', maxWidth: '90px'}} className='pr-editor-total-value-row-item' variant="body1">
                            Line {(material.id +1) * 10}
                                    </Typography>
                                    <Typography className='pr-editor-total-value-row-item' variant="body1">
                                        {conversionFailed && <>
                                            {`${formatCurrencySeparator(material.currency.value, separator, subtotalDecimals)}`}
                                        </>}
                                        {!conversionFailed && <>
                                            {`${formatCurrencySeparator((material.per && !material.freeItem ? material.quantity * material.price / material.per : 1).toFixed(subtotalDecimals), separator, subtotalDecimals)}  ${materials[0].currency.value}`}
                                        </>}
                                    </Typography>
                                    <Typography className='pr-editor-total-value-row-item' variant="body1">
                                        {conversionFailed && (
                                            <>
                                            </>
                                        )}
                                        {(!conversionFailed && (cbThresholdCurrency === material.currency.value)) && (
                                            <>
                                                {`${formatCurrencySeparator((material.per && !material.freeItem ? material.quantity * material.price / material.per : 1), separator, totalPriceDecimals)} ${material.currency.value}`}
                                            </>
                                        )}
                                        {(!conversionFailed && (cbThresholdCurrency !== material.currency.value)) && (
                                            <>
                                                {`${formatCurrencySeparator((material.per && !material.freeItem ? material.quantity * material.price * compCodeCurrToThresholdCurrExchangeRate / material.per : 1).toFixed(subtotalDecimals), separator, subtotalDecimals)} ${cbThresholdCurrency}`}
                                            </>
                                        )}
                                    </Typography>
                                    {showTransportCostConditions && <Typography className='pr-editor-total-value-row-item' variant="body1" >
                                        {`${formatCurrencySeparator(Number((material.per && !material.freeItem ? material.quantity * material.price / material.per : 1).toFixed(subtotalDecimals)) + Number(getTotalConditionPriceForMaterial(material)), separator, totalPriceDecimals)} ${materials[index]?.currency?.value || ''}`}
                                    </Typography>}
                                    {showTaxAmount && (
                                        <Typography className='pr-editor-total-value-row-item' variant="body1">
                                            {`${formatCurrencySeparator(taxValue.toFixed(subtotalDecimals), separator, subtotalDecimals)} ${materials[0].currency.value}`}
                                        </Typography>
                                    )}
                                </div>
                            );
                        })
                }
            </div>
            {conversionFailed && (supplierStatus === 'Validated' || supplierStatus === 'Blank' || supplierStatus === '') &&
                <p style={{color: 'red', fontWeight: '500'}}>
                    {CurrencyController.errorMessageOB(conversionFailed)}
                </p>
            }
        </div>
    );
}

TotalBudget.propTypes = {
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
};
